/**
 * 站内信通知
 * @author ritaxxu
 */

import { Pagination } from './common'

/** 消息级别 */
export enum InnerMsgLevel {
  /** 普通 */
  Normal = 1,
  /** 重要 */
  Important = 2,
}

/** 消息已读状态 */
export enum InnerMsgReadStatus {
  /** 未读 */
  Unread = 0,
  /** 已读 */
  Read = 1,
}

/** 发送方式 */
export enum InnerMsgSendType {
  /** 立即 */
  Immediate = 1,
  /** 单次定时 */
  Scheduled = 2,
  /** cron 周期 */
  Cron = 3,
}

/** 发送来源 */
export enum InnerMsgSourceType {
  /** 手动/定时 */
  Manual = 1,
  /** 自动规则 */
  AutoRule = 2,
}

/** 模板状态 */
export enum InnerMsgTemplateStatus {
  /** 草稿 */
  Draft = 0,
  /** 待触发 */
  Pending = 1,
  /** 已撤销 */
  Cancelled = 3,
  /** cron 启用中 */
  CronActive = 5,
  /** cron 已暂停 */
  CronPaused = 6,
  /** 已完成 */
  Completed = 7,
  /** 系统模板 */
  System = 8,
}

/** 消息列表项（我的消息） */
export interface IInnerMsgItem {
  /** recipient 表主键（调 MarkInnerMsgRead 时传这个） */
  id: number,
  /** 消息实例 ID */
  msgId: number,
  /** 消息标题 */
  title: string,
  /** 消息级别 1=普通 2=重要 */
  level: InnerMsgLevel,
  /** 阅读状态 0=未读 1=已读 */
  readStatus: InnerMsgReadStatus,
  /** 发送时间 */
  sentAt: string,
  /** 阅读时间 */
  readTime: string,
  /** 发送源 1=手动/定时 2=自动规则 */
  sourceType: InnerMsgSourceType,
  /** 自动规则名（sourceType=2 时显示） */
  sourceRuleName: string,
  /** 消息标签列表（后端下发，如 ["线索通知"]）；无标签为空数组 */
  tags: string[],
  /** 渲染后正文 HTML */
  content: string,
  /** 发送人 RTX */
  creator: string,
  /** 附件列表 */
  attachments?: IInnerMsgAttachment[],
}

/** 未读计数 */
export interface IInnerMsgUnreadCount {
  /** 全部未读数 */
  unreadCount: number,
  /** 重要未读数（level=2 且 readStatus=0） */
  importantUnreadCount: number,
  /** 重要未读的 recipientId 列表；len === importantUnreadCount；无排序保证；空时为 [] */
  importantUnreadIds: number[],
}

/** 我的消息列表请求参数 */
export interface IGetInnerMsgListReq {
  /** 0=未读 1=已读；不传不筛选 */
  readStatus?: InnerMsgReadStatus,
  /** 1=普通 2=重要；不传不筛选 */
  level?: InnerMsgLevel,
  page: number,
  pageSize: number,
}

/** 标记已读请求参数 */
export interface IMarkInnerMsgReadReq {
  /** 接收人记录 ID 列表（GetInnerMsgList 返回的 list[].id）；非空进入「按 ID」模式 */
  recipientIds?: number[],
  /** 按级别批量模式：仅允许 1（普通消息）；recipientIds 非空时忽略 */
  level?: InnerMsgLevel,
}

/** 附件对象 */
export interface IInnerMsgAttachment {
  /** 附件原始文件名 */
  name: string,
  /** COS 对象 key */
  cosKey: string,
  /** 文件大小（字节） */
  size: number,
  /** MIME 类型 */
  contentType: string,
}

/** 接收人配置 */
export interface IInnerMsgReceiverConfig {
  /** 公司 ID 列表 */
  companyIds?: number[],
  /** 群组 ID 列表 */
  groupIds?: number[],
  /** 座席 RTX 列表 */
  salesNames?: string[],
}

/** 发送通知请求参数 */
export interface ISendInnerMsgReq {
  /** 标题 */
  title: string,
  /** 正文 HTML */
  content: string,
  /** 附件 */
  attachments?: IInnerMsgAttachment[],
  /** 消息级别 1=普通 2=重要 */
  level: InnerMsgLevel,
  /** 接收人配置 */
  receiverConfig: IInnerMsgReceiverConfig,
  /** 发送方式 1=立即 2=单次定时 3=cron；草稿转发送/恢复时必填 */
  sendType: InnerMsgSendType,
  /** 定时发送时间（sendType=2 必填） */
  scheduledAt?: string,
  /** cron 表达式（sendType=3 必填） */
  cronExpr?: string,
  /**
   * 二期新增：0/不传=新建模板；非 0=对已有模板做「草稿转发送」(status=0) 或「恢复发送」(status=3)。
   * 草稿转发送/恢复场景下 title/content/receiverConfig 可不传，沿用模板已有内容，后端只读 sendType/scheduledAt/cronExpr 做状态流转。
   */
  templateId?: number,
}

/**
 * 保存草稿请求参数（SaveInnerMsgDraft）
 * 宽松保存：所有内容字段允许为空，便于半成品暂存；落库恒为 status=0 草稿态，不触发发送。
 * sendType 若传须在 1-3；templateId=0/不传=新建，非 0=覆盖更新已有草稿。
 */
export interface ISaveInnerMsgDraftReq {
  /** 标题；草稿可空 */
  title?: string,
  /** 正文 HTML；草稿可空 */
  content?: string,
  /** 附件 */
  attachments?: IInnerMsgAttachment[],
  /** 消息级别 1=普通 2=重要；不传默认 1 */
  level?: InnerMsgLevel,
  /** 接收人配置；草稿可空 */
  receiverConfig?: IInnerMsgReceiverConfig,
  /** 发送方式 1=立即 2=单次定时 3=cron；仅记录用于回显，不校验 */
  sendType?: InnerMsgSendType,
  /** 定时时间（sendType=2）；草稿仅暂存不校验未来性 */
  scheduledAt?: string,
  /** cron 表达式（sendType=3）；草稿仅暂存不校验合法性 */
  cronExpr?: string,
  /** 标签列表 */
  tags?: string[],
  /** 0/不传=新建草稿；非 0=覆盖更新已有草稿（须为草稿态手动模板） */
  templateId?: number,
}

/**
 * 再次发送请求参数
 * @deprecated 二期废弃，改用「拷贝(CopyInnerMsg)+草稿转发送」或「恢复发送(SendInnerMsg 的 templateId 分支)」
 */
export interface IResendInnerMsgReq {
  /** 待再次发送的模板 ID（来自 GetSendRecordList list[].templateId） */
  templateId: number,
}

/**
 * 恢复已撤销模板请求参数（ResumeInnerMsg 独立恢复入口）
 * 仅 status=3 已撤销的手动模板可恢复；恢复所需信息（sendType/scheduledAt/cronExpr）取自模板已存字段，无需前端传。
 * 恢复后状态：单次定时 3→1 待触发 / cron 3→5 启用中。
 */
export interface IResumeInnerMsgReq {
  templateId: number,
}

/** 拷贝消息为草稿请求参数 */
export interface ICopyInnerMsgReq {
  /** 被拷贝的源模板 ID（仅手动模板 sourceType=1 支持；本人放行，他人仅超管） */
  sourceTemplateId: number,
}

/** 模板详情请求参数 */
export interface IGetInnerMsgTemplateDetailReq {
  /** 模板 ID（须 > 0；来自发送记录列表 list[].templateId） */
  templateId: number,
}

/**
 * 模板详情响应
 * 模板「配置面」全字段，用于草稿点「发送消息」时回填编辑表单；
 * 累计统计查 GetSendRecordList，触发实例查 GetSendRecordDetail，本接口均不返回。
 */
export interface IGetInnerMsgTemplateDetailRsp {
  templateId: number,
  /** 发送源 1=手动/定时 2=自动规则 */
  sourceType: InnerMsgSourceType,
  /** 创建人 RTX；自动规则模板恒为 system */
  creator: string,
  /** 通知标题 */
  title: string,
  /** 正文 HTML（含占位符原文，未渲染） */
  content: string,
  /** 附件列表；空数组=无附件 */
  attachments?: IInnerMsgAttachment[],
  /** 标签列表；空数组=无标签 */
  tags?: string[],
  /** 消息级别 1=普通 2=重要 */
  level: InnerMsgLevel,
  /** 接收人配置；sourceType=2 时可能为空 */
  receiverConfig?: IInnerMsgReceiverConfig,
  /** 发送方式 1=立即 2=单次定时 3=cron；sourceType=2 或草稿未选时为 null */
  sendType?: InnerMsgSendType,
  /** 定时发送时间；未设置为空串 */
  scheduledAt?: string,
  /** cron 表达式（5 段式）；未设置为空串 */
  cronExpr?: string,
  /** 模板状态 */
  status: InnerMsgTemplateStatus,
  /** 关联自动规则 ID；手动模板为 null */
  sourceRuleId?: number,
  /** 关联规则名；手动模板为空串 */
  sourceRuleName?: string,
  createTime?: string,
  updateTime?: string,
}

/** 发送记录列表请求参数 */
export interface IGetSendRecordListReq {
  /** 0=不限 1=手动/定时 2=自动规则 */
  sourceType?: InnerMsgSourceType,
  /** 创建时间起 */
  createFrom?: string,
  /** 创建时间止 */
  createTo?: string,
  /** 发送人精确筛选（仅持有 ViewInnerMsgSendRecordAuthPoint / IsAdmin 的用户有效，否则后端强制为本人） */
  creatorEqual?: string,
  /** 累计接收人次下限（含，aggregateTotal >= 值） */
  totalRecipientMin?: number,
  /** 累计接收人次上限（含，aggregateTotal <= 值） */
  totalRecipientMax?: number,
  page: number,
  pageSize: number,
}

/** 发送记录列表项 */
export interface ISendRecordItem {
  templateId: number,
  title: string,
  sourceType: InnerMsgSourceType,
  creator: string,
  sourceRuleId?: number,
  sourceRuleName: string,
  /** 消息标签列表（后端下发）；无标签为空数组 */
  tags: string[],
  sendType?: InnerMsgSendType,
  status: InnerMsgTemplateStatus,
  createTime: string,
  lastExecuteAt: string,
  totalRecipient: number,
  readCount: number,
  readRate: number,
}

/** 发送记录详情请求参数 */
export interface IGetSendRecordDetailReq {
  templateId: number,
  page: number,
  pageSize: number,
}

/** 发送记录详情中的触发实例项 */
export interface ISendRecordExecuteItem {
  msgId: number,
  executeRound: number,
  sentAt: string,
  totalRecipient: number,
  readCount: number,
  content: string,
  salesName: string,
  /** 附件列表 */
  attachments?: IInnerMsgAttachment[],
}

/** 发送记录详情响应 */
export interface IGetSendRecordDetailRsp {
  templateId: number,
  title: string,
  sourceType: InnerMsgSourceType,
  sourceRuleId?: number,
  sourceRuleName: string,
  total: number,
  list: ISendRecordExecuteItem[],
}

/** 单次执行接收人列表请求 */
export interface IGetSendRecordExecuteDetailReq {
  /** 消息实例 ID；与 templateId 二选一，推荐传 templateId 免去先拿 msgId */
  msgId?: number,
  /** 模板 ID；后端反查该模板下唯一 msg，草稿/未触发时返回空结果（不报错） */
  templateId?: number,
  /** 已读状态筛选 0=未读 1=已读 */
  readStatus?: InnerMsgReadStatus,
  page: number,
  pageSize: number,
}

/** 单次执行接收人项 */
export interface ISendRecordRecipientItem {
  recipientId: number,
  salesName: string,
  /** 接收人中文名；维表反查不到时为空串，展示回退 salesName */
  salesNameCh?: string,
  companyId: number,
  /** 公司中文名；反查不到时为空串，展示回退 companyId */
  companyName?: string,
  groupId: number,
  /** 群组中文名；反查不到时为空串，展示回退 groupId */
  groupName?: string,
  readStatus: InnerMsgReadStatus,
  /** 阅读时间；未读为空串 */
  readTime: string,
  /** 消息标签快照；无标签为空数组 */
  tags?: string[],
}

/** 单次执行接收人列表响应 */
export interface IGetSendRecordExecuteDetailRsp {
  /** 当前筛选后的总条数（分页用） */
  total: number,
  list: ISendRecordRecipientItem[],
  /** 发送人数（读 msg 冗余字段，不受 readStatus 筛选影响） */
  totalRecipient: number,
  /** 已读人数（不受筛选影响） */
  readCount: number,
  /** 已读率 0~1（readCount/totalRecipient，分母为 0 时返 0）；与 GetSendRecordList.readRate 口径一致 */
  readRate: number,
}

/** 导出单次执行接收人列表请求（异步任务，不带分页） */
export interface IExportSendRecordExecuteDetailReq {
  /** 与 templateId 二选一，推荐传 templateId */
  msgId?: number,
  templateId?: number,
}

/** 组织架构树节点层级 */
export enum OrgTreeNodeLevel {
  /** 公司 */
  Company = 1,
  /** 群组 */
  Group = 2,
  /** 座席 */
  Sales = 3,
}

/** 组织架构树节点 */
export interface IOrgTreeNode {
  /** 节点 ID：公司=companyId 群组=groupId 座席=0 */
  id: number,
  /** 节点名称 */
  name: string,
  /** 层级：1=公司 2=群组 3=座席 */
  level: OrgTreeNodeLevel,
  /** 父公司 ID */
  parentCompanyId: number,
  /** 成员数量 */
  memberCount: number,
  /** 是否叶子节点 */
  isLeaf: boolean,
  /** 子节点列表 */
  children: IOrgTreeNode[],
  /** 座席 RTX 列表（仅叶子节点） */
  salesNames: string[],
}

/** 获取组织架构树响应 */
export interface IGetOrgTreeRsp {
  list: IOrgTreeNode[],
}
